import { useState } from "react";
import axios from "axios";
import {
    Mail,
    Phone,
    User,
    MessageSquare,
    FileText,
    ArrowRight,
} from "lucide-react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";

export default function ContactForm() {
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
    });

    const [success, setSuccess] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const { executeRecaptcha } = useGoogleReCaptcha();

    const handleChange = (e) => {
        setForm({
            ...form,
            [e.target.name]: e.target.value,
        });

        setError("");
        setSuccess("");
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!form.name || !form.email || !form.phone || !form.message) {
            setError("Please fill all required fields");
            return;
        }

        setLoading(true);
        setError("");
        setSuccess("");

        try {
            if (!executeRecaptcha) {
                setError("reCAPTCHA not loaded. Please refresh page.");
                setLoading(false);
                return;
            }

            const token = await executeRecaptcha("contact_form");

            const res = await axios.post(
                "https://jumeiradiscounthotels.com/api/contact",
                {
                    ...form,
                    recaptcha_token: token,
                }
            );

            if (res.data.success) {
                setSuccess("Message sent successfully!");

                setForm({
                    name: "",
                    email: "",
                    phone: "",
                    subject: "",
                    message: "",
                });
            } else {
                setError(
                    res.data.message || "Failed to send message"
                );
            }
        } catch (err) {
            setError(
                err.response?.data?.message ||
                "reCAPTCHA verification failed or server error."
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="relative w-full">

            {/* Decorative Background */}
            <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[#D4A83F]/10 blur-3xl pointer-events-none"></div>

            <div className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full bg-[#174B5F]/10 blur-3xl pointer-events-none"></div>

            {/* FORM CARD */}
            <div className="relative w-full bg-white rounded-[32px] border border-slate-100 shadow-[0_20px_60px_rgba(15,23,42,0.08)] p-6 sm:p-8 lg:p-10 xl:p-12">

                {/* Header */}
                <div className="mb-8 lg:mb-10">

                    <span className="inline-flex items-center px-4 py-2 rounded-full bg-[#D4A83F]/15 text-[#174B5F] text-xs sm:text-sm font-semibold">
                        Contact Us
                    </span>

                    <h3 className="mt-4 text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 leading-tight">
                        Send Us a Message
                    </h3>

                    <p className="mt-3 text-sm sm:text-base text-slate-500 max-w-2xl leading-6">
                        Tell us what you need and our team will get back to you
                        as soon as possible.
                    </p>

                </div>

                {/* Success */}
                {success && (
                    <div className="mb-6 p-4 bg-green-50 border border-green-200 text-green-700 rounded-2xl text-sm">
                        {success}
                    </div>
                )}

                {/* Error */}
                {error && (
                    <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-2xl text-sm">
                        {error}
                    </div>
                )}

                {/* Form */}
                <form
                    onSubmit={handleSubmit}
                    className="space-y-6"
                >

                    {/* Name + Email */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5 lg:gap-6">

                        {/* Name */}
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                Your Name
                            </label>

                            <div className="relative">

                                <User
                                    className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                                    size={18}
                                />

                                <input
                                    type="text"
                                    name="name"
                                    value={form.name}
                                    onChange={handleChange}
                                    placeholder="Enter your name"
                                    className="w-full pl-11 pr-4 py-4 bg-slate-50 border border-slate-200 rounded-2xl text-sm outline-none transition-all focus:bg-white focus:border-[#174B5F] focus:ring-4 focus:ring-[#174B5F]/10"
                                />

                            </div>
                        </div>

                        {/* Email */}
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                Email Address
                            </label>

                            <div className="relative">

                                <Mail
                                    className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                                    size={18}
                                />

                                <input
                                    type="email"
                                    name="email"
                                    value={form.email}
                                    onChange={handleChange}
                                    placeholder="Enter your email"
                                    className="w-full pl-11 pr-4 py-4 bg-slate-50 border border-slate-200 rounded-2xl text-sm outline-none transition-all focus:bg-white focus:border-[#174B5F] focus:ring-4 focus:ring-[#174B5F]/10"
                                />

                            </div>
                        </div>

                    </div>

                    {/* Phone + Subject */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5 lg:gap-6">

                        {/* Phone */}
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                Phone Number
                            </label>

                            <div className="relative">

                                <Phone
                                    className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                                    size={18}
                                />

                                <input
                                    type="tel"
                                    name="phone"
                                    value={form.phone}
                                    onChange={handleChange}
                                    placeholder="Enter your phone"
                                    className="w-full pl-11 pr-4 py-4 bg-slate-50 border border-slate-200 rounded-2xl text-sm outline-none transition-all focus:bg-white focus:border-[#174B5F] focus:ring-4 focus:ring-[#174B5F]/10"
                                />

                            </div>
                        </div>

                        {/* Subject */}
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                Subject
                            </label>

                            <div className="relative">

                                <FileText
                                    className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                                    size={18}
                                />

                                <input
                                    type="text"
                                    name="subject"
                                    value={form.subject}
                                    onChange={handleChange}
                                    placeholder="What can we help with?"
                                    className="w-full pl-11 pr-4 py-4 bg-slate-50 border border-slate-200 rounded-2xl text-sm outline-none transition-all focus:bg-white focus:border-[#174B5F] focus:ring-4 focus:ring-[#174B5F]/10"
                                />

                            </div>
                        </div>

                    </div>

                    {/* Message */}
                    <div>

                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                            Your Message
                        </label>

                        <div className="relative">

                            <MessageSquare
                                className="absolute left-4 top-4 text-slate-400"
                                size={18}
                            />

                            <textarea
                                name="message"
                                rows="8"
                                value={form.message}
                                onChange={handleChange}
                                placeholder="Tell us how we can help..."
                                className="w-full pl-11 pr-4 py-4 bg-slate-50 border border-slate-200 rounded-2xl text-sm outline-none resize-none transition-all focus:bg-white focus:border-[#174B5F] focus:ring-4 focus:ring-[#174B5F]/10"
                            ></textarea>

                        </div>

                    </div>

                    {/* Bottom */}
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5 pt-2">

                        <p className="text-xs text-slate-400">
                            We usually respond within 24 hours.
                        </p>

                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full sm:w-auto min-w-[200px] px-8 py-4 bg-gradient-to-r from-[#002B45] via-[#174B5F] to-[#D4A83F] text-white rounded-2xl font-semibold flex justify-center items-center gap-3 hover:shadow-[0_15px_35px_rgba(23,75,95,0.25)] hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-70 disabled:cursor-not-allowed"
                        >

                            {loading ? (
                                <>
                                    <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                                    Sending...
                                </>
                            ) : (
                                <>
                                    Send Message
                                    <ArrowRight size={18} />
                                </>
                            )}

                        </button>

                    </div>

                </form>

            </div>

        </div>
    );
}